/**
 * Protected Route Component
 *
 * Wraps routes that require authentication
 * Redirects to login page if user is not authenticated
 * Uses cookie-based authentication with backend API validation
 */

import { ReactNode, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useUserProfile } from '../../hooks/useAuth';
import { login, logout } from '../../store/authSlice';
import Loading from '../Loading';

interface ProtectedRouteProps {
  children: ReactNode;
}

/**
 * ProtectedRoute component
 * Checks authentication via backend API (validates httpOnly cookies)
 *
 * @example
 * <ProtectedRoute>
 *   <ProtectedPage />
 * </ProtectedRoute>
 */
export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { data: profile, isLoading, error } = useUserProfile();

  // Sync user profile to Redux when loaded
  useEffect(() => {
    if (profile?.role && profile?.id && profile?.email && profile?.firstName && profile?.lastName) {
      dispatch(login({
        role: profile.role,
        id: profile.id,
        email: profile.email,
        firstName: profile.firstName,
        lastName: profile.lastName,
        activeOrgId: profile.activeOrganizationId,
        photoUrl: profile.photoUrl
      }));
    }
  }, [profile, dispatch]);

  useEffect(() => {
    // If error (401), redirect to login and clear Redux state
    if (error && !isLoading) {
      dispatch(logout());
      const currentPath = window.location.pathname;
      navigate(`/login?redirect=${encodeURIComponent(currentPath)}`, {
        replace: true,
      });
    }
  }, [error, isLoading, navigate, dispatch]);

  // Show loading state while checking authentication. Protected routes render
  // inside the app shell, so the loader fills the content area, not the viewport.
  if (isLoading) {
    return <Loading className="min-h-[60vh]" label="Loading..." />;
  }

  // If not authenticated (error or no profile), don't render children
  if (error || !profile) {
    return null;
  }

  // User is authenticated, render the protected content
  return <>{children}</>;
}
